import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useFormik } from "formik";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { FaPlus, FaRegCircleXmark } from "react-icons/fa6";

import "./createClient.css";
import buttonStyles from "../../../../styles/Button.module.css";

import {
  createUserFormValidate,
  editUserFormValidate,
} from "../../../../helper/validate";
import * as api from "helper/helper";
import AddressTag from "../Address/AddressTag";
import invalidClass, { formikExtra } from "../../helperFunctions/helpers";
import CreateAddressModal from "../Address/CreateAddressModal";
import { PermissionsForSection } from "../auth";
import { Sections } from "../../data/navItem";
import { buildNameWithId } from "helper/tools";
import AutocompleteGo from "../Autocomplete/AutocompleteGo";
import LoadingButton from "../LoadingButton/LoadingButton";

const CreateClient = forwardRef(
  ({ item, reloadClientes, defaultAsociadoId, returnBack }, ref) => {
    const modalRef = useRef();
    const buttonCancelModalRef = useRef();
    const createAddressRef = useRef();

    const [modalData, setModalData] = useState(null);
    const [direcciones, setDirecciones] = useState([]);
    const [isActiveDisabled, setIsActiveDisabled] = useState(true);
    const [isLoading, setIsLoading] = useState(false);

    const [permissions, setPermissions] = useState({
      canCreate: false,
      canEdit: false,
      canVerDetalle: true,
    });

    const [asociados, setAsociados] = useState([
      { id: 0, nombre: "Selecciona un asociado..." },
    ]);

    const [sexos] = useState([
      { id: 0, name: "Selecciona Sexo..." },
      { id: "Hombre", name: "Hombre" },
      { id: "Mujer", name: "Mujer" },
      { id: "x", name: "X" },
    ]);

    const isSaveDisable = useRef();
    const fieldsSetup = useRef({});

    const createMode = item == null || (item && Object.keys(item).length === 0);

    useEffect(() => {
      console.log("---> change item: " + item);
      let isActiveDisable = true;
      if (!createMode) {
        isActiveDisable = false;
      }
      setIsActiveDisabled(isActiveDisable);

      formik?.resetForm();

      setupFields();

      isSaveDisable.current =
        (createMode && !permissions.canCreate) ||
        (!createMode && !permissions.canEdit);

      setDirecciones([]);
    }, [item]);

    useEffect(() => {
      setDirecciones(item?.direcciones ?? []);
    }, [item?.direcciones]);

    useEffect(() => {
      formik?.resetForm();

      api
        .asociados()
        .then((response) => {
          const autoAsociados = response.data.map((item) => {
            const text = buildNameWithId(item, item.numAsociado);
            return { id: item.id, nombre: text };
          });

          console.log(response.status);

          setAsociados(autoAsociados);
        })
        .catch((e) => {
          console.log(e); // "oh, no!"
        });

      const newPermissions = PermissionsForSection(Sections.Clientes);
      setPermissions(newPermissions);
    }, []);

    let initialValues = {
      id: 0,
      activo: true,
      //numCliente: "",
      nombre: "",
      apellidoPaterno: "",
      apellidoMaterno: "",
      nickname: "",
      correo: "",
      celular: "",
      fechaNacimiento: null, // poner en n ull para que no marque error el no requerido
      rfc: "",
      curp: "",
      nacionalidad: "MEXICANA",
      sexo: "X",
      asociadoId: 0,
      direcciones: [],
      asociadosIds: [],
    };

    if (!createMode) {
      let fechaNacimiento = item.fechaNacimiento ?? null;
      if (fechaNacimiento?.length > 0) {
        fechaNacimiento = format(item.fechaNacimiento, "yyyy-MM-dd");
      }

      const autoAsociados = item.asociados.map((item) => {
        const text = buildNameWithId(item, item.numAsociado);
        return { id: item.id, nombre: text };
      });
      //const asociadoId = item?.asociado?.id ?? 0;
      initialValues = { ...item, fechaNacimiento, asociadosIds: autoAsociados };
    } else {
      if (defaultAsociadoId != null) {
        initialValues.asociadoId = defaultAsociadoId;
      }
    }

    const formik = useFormik({
      initialValues,
      enableReinitialize: true,
      validate: createMode ? createUserFormValidate : editUserFormValidate,
      validateOnBlur: false,
      validateOnChange: false,
      onSubmit: async (valuesSource) => {
        setIsLoading(true);

        let values = { ...valuesSource };
        if (direcciones.length > 0) {
          for (let index = 0; index < direcciones.length; index++) {
            let direccion = direcciones[index];
            if (direccion.id > 0) {
              continue;
            }

            delete direccion.id;
            delete direccion.isNew;
          }
          values.direcciones = direcciones;
        }

        values.asociadosIds = values.asociadosIds.map((e) => e.id);

        if (values.id === 0) {
          if (values.fechaNacimiento == null) {
            delete values.fechaNacimiento;
          }
          if (values.curp == null || values.curp.length === 0) {
            delete values.curp;
          }

          const toastId = toast.loading("Creando Cliente...");
          let response = await api.createCliente(values).catch((e) => {
            toast.dismiss(toastId);
            setIsLoading(false);
            if (e.esError && e.codigoError && e.mensaje) {
              toast.error(`Error al crear cliente. ${e.mensaje}`);
              return;
            }
            toast.error("Error al crear cliente.");
          });

          toast.dismiss(toastId);
          setIsLoading(false);

          if (!response) return;

          const nombre = buildNameWithId(
            response.data,
            response.data.numCliente,
          );

          toast.success("Cliente creado.");
          reloadClientes({
            newClienteId: response.data.id,
            newAsociadoId: formik.values.asociadoId,
            nombre: nombre,
          });

          buttonCancelModalRef.current.click();
        } else {
          const toastId = toast.loading("Actualizando Cliente...");
          const response = await api
            .updateCliente(values.id, values)
            .catch((e) => {
              toast.dismiss(toastId);
              setIsLoading(false);
              if (e.esError && e.codigoError && e.mensaje) {
                toast.error(`Error al Actualizar. ${e.mensaje}`);

                return;
              }

              toast.error("Error al actualizar cliente.");
            });
          if (!response) return;

          const responseActivo = await api
            .activarCliente(values.id, values.activo)
            .catch((e) => {
              toast.dismiss(toastId);
              setIsLoading(false);
              toast.error("Error al actualizar activo cliente.");
            });

          toast.dismiss(toastId);
          setIsLoading(false);

          if (!responseActivo) return;

          toast.success("Cliente actualizado.");
          reloadClientes();

          buttonCancelModalRef.current.click();
        }
      },
    });

    useImperativeHandle(ref, () => ({
      getValue: () => {
        return formik.values;
      },
      toggleModal: () => {
        formik?.resetForm();
        modalRef.current.click();
      },
    }));

    /*
    AddressTag handle
    */
    function createEditAddress(event, item = null) {
      setModalData(item);
      createAddressRef.current.toggleModal();
    }

    function deleteAddress(event, deleteItem) {
      const updated = direcciones.filter((item) => item.id !== deleteItem.id);
      setDirecciones(updated);
      //formik.setFieldValue("direcciones", updated);
      //formik.values.direcciones = updated;
    }

    /** manage address add/edit */
    function handleAddressViewChanges(item) {
      /*
      item.isNew
      item.id -> contiene {num} = editado
      */
      if (item.isNew) {
        delete item.isNew;
        item.id = -Date.parse(Date());
        const newDirecciones = direcciones.concat(item);
        setDirecciones(newDirecciones);
      } else {
        const index = direcciones.findIndex((value) => item.id === value.id);
        if (index !== -1) {
          direcciones.splice(index, 1, item);
          const allDirecciones = [...direcciones];
          setDirecciones(allDirecciones);
        }
      }
    }

    function setupFields() {
      // fieldsSetup
      fieldsSetup.current = {};

      for (const [key, value] of Object.entries(initialValues)) {
        console.log(`${key}: ${value}`);

        const res = api.isFieldReadOnlyWith(createMode, value);
        fieldsSetup.current[key] = res;
      }
    }

    return (
      <>
        <button
          ref={modalRef}
          type="button"
          className="btn btn-primary"
          data-bs-toggle="modal"
          data-bs-target="#staticBackdropCrearCliente"
          hidden
        >
          Abrir Crear clientes
        </button>
        <div
          className="modal fade modal-xl"
          id="staticBackdropCrearCliente"
          data-bs-backdrop="static"
          data-bs-keyboard="false"
          tabIndex="-1"
          aria-labelledby="staticBackdropCrearClienteLabel"
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5
                  className="modal-title"
                  id="staticBackdropCrearClienteLabel"
                >
                  {createMode ? "Crear" : "Editar"} Cliente
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  onClick={(e) => {
                    if (returnBack != null) returnBack();
                  }}
                ></button>
              </div>

              <div className="modal-body">
                <form
                  className="row g-3"
                  onSubmit={(event) => {
                    event.preventDefault();
                    return false;
                  }}
                >
                  <div className="bd-example-row1">
                    <div className="row">
                      <div className="col-9">
                        <label
                          htmlFor="validationApellidoPaterno"
                          className="form-label"
                        >
                          Nombre
                        </label>
                        <input
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.nombre,
                          )}`}
                          id="validationUser"
                          aria-describedby="eeee"
                          {...formik.getFieldProps("nombre")}
                          {...formikExtra.upperCase("nombre", formik)}
                        />
                        <div className="valid-feedback">Looks good!</div>
                        <div className="invalid-feedback">
                          {formik.errors.nombre}
                        </div>
                      </div>

                      <div className="col align-container-center">
                        <div className="container-center-horizontal">
                          <div className="form-check form-switch container-center-vertical">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="flexSwitchCheckDefault"
                              {...formik.getFieldProps("activo")}
                              checked={formik.values.activo}
                              disabled={
                                isActiveDisabled ||
                                !permissions.canActivar ||
                                fieldsSetup.current.activo
                              }
                            />
                            <span>
                              <label
                                className="form-check-label"
                                htmlFor="flexSwitchCheckDefault"
                              >
                                Activo
                              </label>
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="">
                        <label
                          htmlFor="validationApellidoPaterno"
                          className="form-label"
                        >
                          Apellido Paterno
                        </label>
                        <input
                          key="CCApellidoPaterno"
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.apellidoPaterno,
                          )}`}
                          id="validationApellidoPaterno"
                          aria-describedby="eeee"
                          {...formik.getFieldProps("apellidoPaterno")}
                          {...formikExtra.upperCase("apellidoPaterno", formik)}
                        />
                        <div className="valid-feedback">Looks good!</div>
                        <div className="invalid-feedback">
                          {formik.errors.apellidoPaterno}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="">
                        <label
                          htmlFor="validationApellidoMaterno"
                          className="form-label"
                        >
                          Apellido Materno
                        </label>
                        <input
                          key="CCApellidoMaterno"
                          type="text"
                          className="form-control"
                          id="validationApellidoMaterno"
                          aria-describedby="emailHelp"
                          {...formik.getFieldProps("apellidoMaterno")}
                          {...formikExtra.upperCase("apellidoMaterno", formik)}
                        />
                        <div className="valid-feedback">Looks good!</div>
                        <div className="invalid-feedback"></div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col align-self-start">
                        <label
                          htmlFor="validationApellidoPaterno"
                          className="form-label"
                        >
                          Alias
                        </label>
                        <input
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.nickname,
                          )}`}
                          id="validationUser"
                          aria-describedby="eeee"
                          {...formik.getFieldProps("nickname")}
                          {...formikExtra.upperCase("nickname", formik)}
                        />
                        <div className="valid-feedback">Looks good!</div>
                        <div className="invalid-feedback">
                          {formik.errors.nickname}
                        </div>
                      </div>

                      <div className="col align-self-start">
                        <label htmlFor="validationDOB" className="form-label">
                          Fecha de Nacimiento
                        </label>
                        <input
                          type="date"
                          min="1900-01-01"
                          className={`form-control ${invalidClass(
                            formik.errors.fechaNacimiento,
                          )}`}
                          id="validationDOB"
                          aria-describedby="emailHelperwerwer"
                          placeholder="YYYY-MM-DD"
                          {...formik.getFieldProps("fechaNacimiento")}
                          value={formik.values.fechaNacimiento}
                          onChange={(e) => {
                            formik.setFieldValue(
                              "fechaNacimiento",
                              e.target.value,
                            );
                          }}
                        />
                        <div className="valid-feedback">Looks good!</div>
                        <div className="invalid-feedback">
                          {formik.errors.fechaNacimiento}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col align-self-start">
                        <label htmlFor="validationEmail" className="form-label">
                          Email
                        </label>
                        <input
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.correo,
                          )}`}
                          id="validationEmail"
                          aria-describedby="correo"
                          {...formik.getFieldProps("correo")}
                          {...formikExtra.upperCase("correo", formik)}
                        />
                        <div className="valid-feedback">Looks good!</div>
                        <div className="invalid-feedback">
                          {formik.errors.correo}
                        </div>
                      </div>
                      <div className="col align-self-start">
                        <label htmlFor="validationEmail" className="form-label">
                          Celular
                        </label>
                        <input
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.celular,
                          )}`}
                          id="validationEmail"
                          aria-describedby="emailHelp"
                          {...formik.getFieldProps("celular")}
                          {...formikExtra.upperCase("celular", formik)}
                        />
                        <div className="valid-feedback">
                          Looks good! WPWPWPW
                        </div>
                        <div className="invalid-feedback">
                          {formik.errors.celular}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col align-self-start">
                        <label
                          htmlFor="exampleInputEmail1"
                          className="form-label"
                        >
                          RFC
                        </label>
                        <input
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.rfc,
                          )}`}
                          id="validationEmail"
                          aria-describedby="emailHelp777"
                          {...formik.getFieldProps("rfc")}
                          {...formikExtra.upperCase("rfc", formik)}
                        />
                      </div>
                      <div className="col align-self-end">
                        <label
                          htmlFor="exampleInputEmail1"
                          className="form-label"
                        >
                          CURP
                        </label>
                        <input
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.curp,
                          )}`}
                          id="validationEmail"
                          aria-describedby="emailHelp777"
                          {...formik.getFieldProps("curp")}
                          {...formikExtra.upperCase("curp", formik)}
                        />
                        <div className="valid-feedback"></div>
                        <div className="invalid-feedback">
                          {formik.errors.curp}
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <label
                          htmlFor="exampleInputEmail1"
                          className="form-label"
                        >
                          Nacionalidad
                        </label>
                        <input
                          {...formik.getFieldProps("nacionalidad")}
                          {...formikExtra.upperCase("nacionalidad", formik)}
                          type="text"
                          className={`form-control ${invalidClass(
                            formik.errors.nacionalidad,
                          )}`}
                          id="validationEmail"
                          aria-describedby="emailHelp777"
                        />
                        <div id="emailHelp" className="form-text"></div>
                      </div>
                      <div className="col">
                        <label
                          htmlFor="exampleInputEmail1"
                          className="form-label"
                        >
                          Sexo
                        </label>
                        <select
                          className="form-select"
                          aria-label="Default select example"
                          onChange={(item) => {
                            formik.setFieldValue(
                              "sexo",
                              item.currentTarget.value,
                            );
                          }}
                        >
                          {sexos.map((item, index) => {
                            return (
                              <option
                                selected={item.name === formik.values.sexo}
                                value={index}
                              >
                                {item.name}
                              </option>
                            );
                          })}
                        </select>
                        <div className="valid-feedback"></div>
                        <div className="invalid-feedback"></div>
                      </div>
                    </div>

                    <div className="separator"></div>
                    <div className="row">
                      <label htmlFor="exampleDataList" className="form-label">
                        Asociado
                      </label>

                      {/*<select
                      className={`form-select ${invalidClass(
                        formik.errors.asociadoId
                      )}`}
                      id="inputGroupSelect01"
                      {...formik.getFieldProps("asociadoId")}
                      onChange={(e) => {
                        formik.setFieldValue(
                          "asociadoId",
                          parseInt(e.target.value)
                        );
                      }}
                    >
                      {asociados.map((item) => {
                        const text = buildNameWithId(item, item.numAsociado);
                        return <option value={item.id}>{text}</option>;
                      })}
                    </select>*/}
                      <div className="input-group">
                        <AutocompleteGo
                          options={asociados}
                          placeholder="Buscar asociado..."
                          className={`form-select ${invalidClass(
                            formik.errors.asociadoId,
                          )}`}
                          value={formik.values.asociadoId}
                          onChange={(e) => {
                            //formik.setFieldValue("sucursal", e);
                            console.log(e);
                            const id = e == null ? 0 : parseInt(e.id);
                            formik.setFieldValue("asociadoId", id);
                          }}
                          style={{ flex: "1" }}
                        >
                          <div className="valid-feedback">Looks good!</div>
                          <div className="invalid-feedback">
                            {formik.errors.asociadoId}
                          </div>
                        </AutocompleteGo>
                        <div>
                          <button
                            className="btn btn-primary"
                            onClick={() => {
                              const asociado = asociados.find(
                                (e) => e.id === formik.values.asociadoId,
                              );
                              if (asociado == null) return;

                              // ya agregado ignorar
                              const nuevoAsociado =
                                formik.values.asociadosIds.find(
                                  (e) => e.id === formik.values.asociadoId,
                                );
                              if (nuevoAsociado != null) return;

                              formik.setFieldValue("asociadoId", 0);
                              formik.setFieldValue("asociadosIds", [
                                asociado,
                                ...formik.values.asociadosIds,
                              ]);
                            }}
                          >
                            <div className={buttonStyles.icon_button}>
                              <FaPlus />
                              Agregar
                            </div>
                          </button>
                        </div>
                      </div>

                      <div className=" mt-3 px-3 list-group ">
                        {formik.values.asociadosIds.map((e, index) => {
                          return (
                            <>
                              <a
                                href="#"
                                className="list-group-item list-group-item-action d-flex align-items-center"
                                aria-current="true"
                              >
                                <span style={{ flex: "1" }}>{e.nombre}</span>

                                <a className="page-link" href="#">
                                  <FaRegCircleXmark />
                                </a>
                              </a>
                            </>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="">
                    <div className="separator">
                      <h5 className="card-title">
                        Direcciones
                        <span>
                          <button
                            className="btn btn-primary"
                            style={{ marginLeft: "20px" }}
                            onClick={(event) => createEditAddress(event, {})}
                            disabled={fieldsSetup.current.direcciones}
                          >
                            <div className={buttonStyles.icon_button}>
                              <FaPlus />
                              Agregar
                            </div>
                          </button>
                        </span>
                      </h5>
                      {direcciones?.map((item, index) => {
                        const datas = [
                          item.nombre,
                          item.cp ?? "",
                          item.calle ?? "",
                          item.estado ?? "",
                          item.ciudad ?? "",
                          item.noExterior ?? "",
                          item.noInterior ?? "",
                          item.referencia ?? "",
                        ];
                        const filtered = datas.filter((item) => item);
                        const value = filtered.join(", ");
                        return (
                          <AddressTag
                            key={index}
                            value={value}
                            item={item}
                            createEditAddress={createEditAddress}
                            deleteAddress={deleteAddress}
                            isDisable={fieldsSetup.current.direcciones}
                          />
                        );
                      })}
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  ref={buttonCancelModalRef}
                  onClick={(e) => {
                    if (returnBack != null) returnBack();
                  }}
                >
                  Cancelar
                </button>
                <LoadingButton
                  style={{ height: "38px" }}
                  onClick={(e) => {
                    formik.handleSubmit();
                    // Fetch all the forms we want to apply custom Bootstrap validation styles to
                  }}
                  text={"Guardar"}
                  loadingText={"Guardando..."}
                  isLoading={isLoading}
                  isDisable={isSaveDisable.current}
                />
              </div>
            </div>
          </div>
        </div>

        <CreateAddressModal
          ref={createAddressRef}
          returnToModalId="staticBackdropCrearCliente"
          item={modalData}
          reloadDireccion={handleAddressViewChanges}
        />
      </>
    );
  },
);

export default CreateClient;
